import { ContentEnum } from "@/enums/content";
import { getTimeStampForIST, showCustomMessage } from "@/methods/utils";
import ApiUnverified from "@/services/apiUnverified";
import { useSessionStore } from "@/stores/sessionStore";
import { ContentChildrenInputs } from "@/types/content";
import { CommonStore, IdNamePair } from "@/types/global";
import { parseISO } from "date-fns";

import { defineStore, storeToRefs } from "pinia";

function getInitialCommonStore(): CommonStore {
  const initialCommonStore: CommonStore = {
    Cities: null,
    States: null,
    AcademicSessions: null,
    CourseClasses: {},
    ServerClockTimerId: null,
    ServerClockTimeStamp: 0,
  };
  return JSON.parse(JSON.stringify(initialCommonStore));
}
export const useCommonStore = defineStore("commonStore", {
  state: (): CommonStore => getInitialCommonStore(),
  getters: {
    ServerClock(): Date {
      return new Date(this.ServerClockTimeStamp);
    },
  },
  actions: {
    async initializeServerClock() {
      if (this.ServerClockTimerId !== null) {
        clearInterval(this.ServerClockTimerId);
        this.ServerClockTimerId = null;
        this.ServerClockTimeStamp = 0;
      }
      let isSuccess = false;
      const response = await new ApiUnverified().getStrictServerTime();
      if (response.isSuccess) {
        this.ServerClockTimeStamp = getTimeStampForIST(parseISO(response.data));
        this.ServerClockTimerId = setInterval(() => {
          if (this.ServerClockTimeStamp !== null) {
            this.ServerClockTimeStamp += 100;
          }
        }, 100);
        isSuccess = true;
      }
      return isSuccess;
    },
    async getCities() {
      if (this.Cities === null) {
        const sessionStore = useSessionStore();
        const { allApiVerified } = storeToRefs(sessionStore);
        const response = await allApiVerified.value.apiVerified.getCities();
        if (response.isSuccess) {
          this.Cities = response.data;
        } else {
          showCustomMessage({
            messageText: "An error occured getting list of cities.",
            messageType: "error",
          });
          this.Cities = [];
        }
      }
      return this.Cities ? this.Cities : [];
    },
    async getStates() {
      if (this.States === null) {
        const sessionStore = useSessionStore();
        const { allApiVerified } = storeToRefs(sessionStore);
        const response = await allApiVerified.value.apiVerified.getStates();
        if (response.isSuccess) {
          this.States = response.data;
        } else {
          showCustomMessage({
            messageText: "An error occured getting list of States.",
            messageType: "error",
          });
          this.States = [];
        }
      }
      return this.States ? this.States : [];
    },
    async getAcademicSessions(onlyActive: boolean) {
      const sessionStore = useSessionStore();
      const { allApiVerified } = storeToRefs(sessionStore);
      if (this.AcademicSessions === null) {
        const response = await allApiVerified.value.apiVerified.getAcademicSessions(false);
        if (response.isSuccess) {
          this.AcademicSessions = response.data;
        } else {
          showCustomMessage({
            messageText: "An error occured getting list of academic sessions",
            messageType: "error",
          });
          this.AcademicSessions = [];
        }
      }
      const academicSessions: Array<IdNamePair> = [];
      if (this.AcademicSessions) {
        const currentYear = (await new ApiUnverified().getServerTime()).getFullYear();
        this.AcademicSessions.forEach((as) => {
          const asYear = parseInt(as.Name.split("-")[0]);
          if (!onlyActive || asYear >= currentYear - 1) {
            academicSessions.push(as);
          }
        });
      }
      return academicSessions ? academicSessions : [];
    },
    async getClassesForCourse(affiliationId: number, courseId: number) {
      if (this.CourseClasses[courseId]) {
        return this.CourseClasses[courseId];
      }
      const sessionStore = useSessionStore();
      const { allApiVerified } = storeToRefs(sessionStore);
      const contentInputs: ContentChildrenInputs = {
        selectedContent: {
          CourseId: courseId,
          ClassId: null,
          SubjectId: null,
          TopicId: null,
          CourseChapterId: null,
          KSCClusterId: null,
          KSCId: null,
        },
        userId: 0,
        isStudent: false,
        onlyActive: true,
        skipTopics: true,
        affiliationId: affiliationId,
        parentContentType: ContentEnum.COURSE,
      };
      const response = await allApiVerified.value.contentApi.getContentChildren(contentInputs);
      if (!response.isSuccess) {
        showCustomMessage({
          messageText: "An error occured getting list of classes for course.",
          messageType: "error",
        });
        return [];
      }
      this.CourseClasses[courseId] = response.data;
      return this.CourseClasses[courseId];
    },
  },
});
